import React from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-xl sm:text-2xl font-bold text-[#0B2B62]">{title}</h2>
    <div className="mt-3 space-y-3 text-slate-700 leading-relaxed text-[15px]">{children}</div>
  </section>
);

export default function PoliticaPrivacidade() {
  usePageTitle(
    "Política de Privacidade",
    "Como a RAF Direct coleta, usa e protege dados pessoais em conformidade com a LGPD (Lei 13.709/2018). Seus direitos como titular de dados."
  );
  return (
    <div className="bg-white" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Documento legal</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0B2B62]">Política de Privacidade</h1>
        <p className="mt-3 text-sm text-slate-500">
          Última atualização: 05 de fevereiro de 2026 · Em conformidade com a LGPD (Lei 13.709/2018)
        </p>

        <Section title="1. Quem somos">
          <p>
            A <strong>RAF Direct</strong> é uma plataforma digital brasileira de orientação, informação
            e encaminhamento de famílias a clínicas e profissionais credenciados. Esta Política de
            Privacidade descreve como coletamos, usamos, armazenamos e compartilhamos dados pessoais
            no site <a href="https://rafdirect.com.br" className="text-[#0B2B62] underline">rafdirect.com.br</a>.
          </p>
          <p>
            Para dados institucionais completos (razão social, CNPJ e contato do encarregado),
            consulte a página <Link to="/compliance" className="text-[#0B2B62] underline">Compliance</Link>.
          </p>
        </Section>

        <Section title="2. Quais dados coletamos">
          <p>Coletamos apenas o mínimo necessário para prestar o serviço:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Dados de identificação</strong>: nome, e-mail, telefone, CPF (quando aplicável).</li>
            <li><strong>Dados de contato do caso</strong>: informações fornecidas voluntariamente na triagem.</li>
            <li><strong>Dados de navegação</strong>: endereço IP, tipo de dispositivo, páginas visitadas, cookies.</li>
            <li><strong>Dados de pagamento</strong>: processados exclusivamente por gateway certificado (Stripe). Não armazenamos números de cartão.</li>
          </ul>
        </Section>

        <Section title="3. Bases legais (Art. 7º da LGPD)">
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Consentimento</strong> — dados fornecidos por você em cadastros e triagens.</li>
            <li><strong>Execução de contrato</strong> — para prestar o serviço solicitado.</li>
            <li><strong>Cumprimento de obrigação legal</strong> — fiscal, regulatória, judicial.</li>
            <li><strong>Legítimo interesse</strong> — segurança, prevenção a fraude, melhoria do produto.</li>
          </ul>
        </Section>

        <Section title="4. Como usamos seus dados">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Encaminhar seu caso a consultores, clínicas ou profissionais credenciados.</li>
            <li>Enviar comunicações operacionais (e-mail, WhatsApp, SMS) sobre seu atendimento.</li>
            <li>Emitir comprovantes, propostas e documentos relacionados ao serviço.</li>
            <li>Cumprir obrigações legais e responder a autoridades competentes.</li>
            <li>Analisar o uso da plataforma de forma agregada e anônima para melhorias.</li>
          </ul>
        </Section>

        <Section title="5. Compartilhamento de dados">
          <p>Compartilhamos dados apenas quando estritamente necessário, com:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Parceiros credenciados</strong> (clínicas, profissionais) — para prestar o atendimento que você solicitou.</li>
            <li><strong>Fornecedores de tecnologia</strong> — hospedagem, envio de e-mail (SendGrid), mensagens (Twilio), pagamentos (Stripe), analytics (Google, Meta).</li>
            <li><strong>Autoridades</strong> — quando exigido por lei ou ordem judicial.</li>
          </ul>
          <p>Nunca vendemos dados pessoais.</p>
        </Section>

        <Section title="6. Cookies e tecnologias similares">
          <p>
            Utilizamos cookies próprios e de terceiros para funcionamento essencial, medição de audiência
            e marketing (Google Analytics, Google Ads, Meta Pixel). Você pode gerenciar cookies pelo seu
            navegador. A recusa não impede a navegação, mas pode limitar funcionalidades.
          </p>
        </Section>

        <Section title="7. Retenção">
          <p>
            Mantemos dados pelo prazo necessário à finalidade e ao cumprimento de obrigações legais
            (em regra, até 5 anos após o encerramento do vínculo, salvo determinação diversa).
          </p>
        </Section>

        <Section title="8. Segurança">
          <p>
            Aplicamos criptografia em trânsito (HTTPS/TLS), armazenamento em ambiente controlado,
            controle de acesso por perfil e auditoria com registros hash-chained. Nenhum sistema é
            100% invulnerável, mas adotamos as melhores práticas do setor.
          </p>
        </Section>

        <Section title="9. Seus direitos (Art. 18 da LGPD)">
          <p>Você pode solicitar, a qualquer momento:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Confirmação da existência de tratamento;</li>
            <li>Acesso aos dados;</li>
            <li>Correção de dados incompletos, inexatos ou desatualizados;</li>
            <li>Anonimização, bloqueio ou eliminação de dados desnecessários;</li>
            <li>Portabilidade;</li>
            <li>Eliminação dos dados tratados com base no consentimento;</li>
            <li>Informação sobre entidades com as quais compartilhamos dados;</li>
            <li>Revogação do consentimento.</li>
          </ul>
          <p>
            As solicitações devem ser enviadas para
            {" "}<a href="mailto:contato@rafdirect.com.br" className="text-[#0B2B62] underline">contato@rafdirect.com.br</a>.
            Responderemos em até 15 dias.
          </p>
        </Section>

        <Section title="10. Menores de idade">
          <p>
            Quando o atendimento envolver menores, os dados são fornecidos e autorizados por
            responsáveis legais. Não coletamos dados de menores diretamente.
          </p>
        </Section>

        <Section title="11. Encarregado (DPO)">
          <p>
            O Encarregado pelo Tratamento de Dados Pessoais da RAF Direct pode ser contactado pelo
            e-mail <a href="mailto:contato@rafdirect.com.br" className="text-[#0B2B62] underline">contato@rafdirect.com.br</a>.
          </p>
        </Section>

        <Section title="12. Alterações">
          <p>
            Esta Política pode ser atualizada periodicamente. A versão vigente estará sempre publicada
            nesta página, com data de atualização no topo.
          </p>
        </Section>

        <div className="mt-12 border-t pt-6 text-sm text-slate-500">
          Dúvidas? Escreva para
          {" "}<a href="mailto:contato@rafdirect.com.br" className="text-[#0B2B62] underline">contato@rafdirect.com.br</a>.
        </div>
      </div>
    </div>
  );
}
